import * as stateObjects from './stateObjects';
import { isNullOrEmpty, copyObject } from '../modules/utils';

export const setWindowWidth = state => {
  return state.windowWidth = window.innerWidth;
}

export const addConstants = (state, constants) => {
  const noWhiteSpace = RegExp(/\s/g);

  constants.questionTypes.forEach(qt => {
    const constantName = qt.name.replace(noWhiteSpace, '_').toUpperCase();
    state.constants.questionTypes[constantName] = qt.id;

    qt.options.forEach(o => {
      const optionName = constantName + '_' + o.displayName.replace(noWhiteSpace, '_').toUpperCase();
      state.constants.questionTypeOptions[optionName] = o.id;
    });
  })

  constants.displayTypes.forEach(dt => {
    const constantName = dt.name.replace(noWhiteSpace, '_').toUpperCase();
    state.constants.displayTypes[constantName] = dt.id;

    dt.options.forEach(o => {
      const optionName = constantName + '_' + o.displayName.replace(noWhiteSpace, '_').toUpperCase();
      state.constants.displayTypeOptions[optionName] = o.id;
    });
  })

  // Add any non-payload constants here, i.e., constants not from the server
  {
    // Constants for the process.env.VUE_APP_COMPLETENESS_CHECK define.
    const names = ["none", "end of survey", "per section"];
    for (let i = 0; i < names.length; i++) {
      const name = names[i].replace(noWhiteSpace, '_').toUpperCase();
      state.constants.completenessValidationTypes[name] = i.toString();
    }
  }
}

export const updateConstants = (state, constants) => {
  if (constants.hasOwnProperty("roleLevels")) {
    state.constants.roleLevels = {...constants.roleLevels};
  }
}

export const addSurveys = (state, surveys) => {
  // map the survey name onto each instance for UI display.
  surveys.forEach(survey => {
    survey.orgSurveys.map(orgSurvey => orgSurvey.surveyName = survey.surveyName)
  });

  state.surveys = surveys;
  state.orgSurveys = surveys.flatMap(survey => survey.orgSurveys);
}

export const addActiveSurvey = (state, survey) => {
  let template = Object.assign({}, stateObjects.activeSurvey());

  state.activeSurvey = Object.assign(template, survey);
  state.activeSurveyNavSections = survey.sections.map(section => {
    return {
      id: section.hspSectionId,
      name: section.sectionName,
      ordinal: section.sectionOrdinal,
      viewed: false,
      triggerSectionId: null,
      targetSectionId: null,
      questionIds: []
    }
  });

  for(const displayType in state.constants.displayTypes) {
    const displayTypeId = state.constants.displayTypes[displayType];
    let displayTypes = state.activeSurvey.displayOptions
      .filter(displayOption => displayOption.displayTypeId === displayTypeId);

    state.activeSurvey.displayOptionsMap[displayTypeId] = displayTypes;
  }

  let option = state.activeSurvey.displayOptions
    .find(data => data.optionName === 'primaryColorScheme');

  if (option !== undefined) {
    state.activeColorScheme = option.optionValue;
  }
}

export const removeActiveSurvey = state => {
  state.activeSurvey = {
    displayOptionsMap: {},
    displayOptions: [],
    sections: []
  };
}

export const addInstanceInfo = (state, instanceInfo) => {
  state.auth.instanceInfo = instanceInfo;
}

export const updateSurveyUser = (state, surveyUser) => {
  state.surveyUser.responses = [];
  state.surveyUser = Object.assign(state.surveyUser, surveyUser);

  if(!(surveyUser.hasOwnProperty('responses') && Array.isArray(surveyUser.responses))) {
    surveyUser.responses = [];
  }
  else if(surveyUser.responses.length > 0) {
    state.activeSurveyNavSections.forEach(section => {
      if(state.surveyUser.responses.findIndex(response => response.hspSectionId === section.id) >= 0) {
        section.viewed = true;
      }
      else if(section.id === state.surveyUser.lastSectionId) {
        section.viewed = true;
      }
    });
  }
}

export const updateSurveyUserToken = (state, payload) => {
  state.surveyUser.token = payload.token;
  state.surveyUser.validTo = payload.validTo;
}

export const setUserStatus = (state, payload) => {
  state.surveyUser.userSurveyStatusId = payload.status;
}

export const setActiveSectionId = (state, sectionId) => {
  if(state.activeSectionId != sectionId) {
    state.previousSectionId = state.activeSectionId;
    state.activeSectionId = sectionId;
  }
}

export const setSectionViewedStatus = (state, payload) => {
  let section = state
    .activeSurveyNavSections
    .find(section => section.id === payload.id);

  if(section) {
    section.viewed = payload.viewed;
  }
}

export const setNavSectionQuestionIds = (state, payload) => {
  let section = state
    .activeSurveyNavSections
    .find(section => section.id === payload.id);

  section.questionIds = payload.questionIds;
}

export const setTargetSection = (state, payload) => {
  let section = state
    .activeSurveyNavSections
    .find(section => section.id === payload.triggerSectionId);
  let targetSection = state
    .activeSurveyNavSections
    .find(section => section.id === payload.targetSectionId);

  // Prevent exception errors by checking if the objects exist before setting their properties.
  // Either payload triggerSectionId and/or targetSectionId might be null. The most likely null would be the 
  // triggerSectionId due to session timed out, user re-logged back into subsequent session(s), or user refreshed the browser.
  if (section) {
    section.targetSectionId = payload.targetSectionId;
  }
  if (targetSection) {
    targetSection.triggerSectionId = payload.triggerSectionId;
  }
}

export const clearTargetSectionId = (state, sectionId) => {
  let triggerSection = state
    .activeSurveyNavSections
    .find(section => section.id === sectionId);
  let targetSection = state
    .activeSurveyNavSections
    .find(section => section.id === triggerSection.targetSectionId);

  if(triggerSection) {
    triggerSection.targetSectionId = null;
  }

  if(targetSection) {
    targetSection.triggerSectionId = null;
  }
}

export const setSurveyInitialized = (state, payload) => {
  state.activeSurveyInitialized = payload;
}

export const setQuestionResponse = (state, response) => {
  let newResponse = createResponse(response);
  let responseIndex = state
    .surveyUser
    .responses
    .findIndex(userResponse => userResponse.hspCategoryQuestionId == response.questionId);

  if(responseIndex > -1) {
    let stateResponse = Object.assign({}, state.surveyUser.responses[responseIndex]);
    newResponse = Object.assign(stateResponse, newResponse);
    state.surveyUser.responses.splice(responseIndex, 1, newResponse);
  }
  else {
    state.surveyUser.responses.push(newResponse);
  }
}

export const setCheckboxesQuestionResponse = (state, response) => {
  let responseIndex = -1;

  if(response.isExclusive && response.include) {
    // Exclusive response will remove all other responses to the question.
    state.surveyUser.responses = state
      .surveyUser
      .responses
      .filter(userResponse => userResponse.hspCategoryQuestionId !== response.questionId);

      state.surveyUser.responses.push(createResponse(response));
    return;
  }
  else if(!response.isExclusive) {
    // Non exclusive response will remove exclusive response from the question.
    responseIndex = state
      .surveyUser
      .responses
      .findIndex(userResponse => userResponse.hspCategoryQuestionId === response.questionId && userResponse.hspResponseTypeId === 1)

    if(responseIndex >= 0) {
      state.surveyUser.responses.splice(responseIndex, 1);
    }
  }

  responseIndex = state
    .surveyUser
    .responses
    .findIndex(userResponse => {
      return userResponse.hspCategoryQuestionId == response.questionId && userResponse.hspResponseId == response.responseId
    });

  if(response.include === true && responseIndex === -1) {
    state.surveyUser.responses.push(createResponse(response));
  }
  else if(response.include === false && responseIndex >= 0) {
    state.surveyUser.responses.splice(responseIndex, 1);
  }
}

export const setCheckboxTextResponse = (state, response) => {
  let responseIndex = state
    .surveyUser
    .responses
    .findIndex(userResponse => userResponse.hspCategoryQuestionId === response.questionId && userResponse.hspResponseId === response.responseId)
  let stateResponse = createResponse(response);

  if(responseIndex === -1) {
    state.surveyUser.responses.push(stateResponse);
  }
  else if(responseIndex >= 0) {
    state.surveyUser.responses.splice(responseIndex, 1, stateResponse);
  }
}

function createResponse(response) {
  let responseTypeId = getResponseType({
    isExclusive: response.isExclusive,
    hasTextField: response.hasTextField
  });

  let obj = {
    hspQuestionTypeId: response.questionTypeId,
    hspCategoryQuestionId: response.questionId
  };

  if(responseTypeId !== null) {
    obj.hspResponseTypeId = responseTypeId;
  }

  if(response.hasOwnProperty('responseId')) {
    obj.hspResponseId = response.responseId
  }

  if(response.hasOwnProperty('responseText')) {
    obj.responseTextValue = response.responseText;
  }

  return obj;
}

function getResponseType(params) {
  let responseTypeId = null;

  if(params.isExclusive === true) {
    responseTypeId = 1
  }
  else if(params.hasTextField === true) {
    responseTypeId = 2
  }

  return responseTypeId;
}

export const addStatistics = (state, report) => {
  state.activeSurveyReports.statistics = report;
}

export const addAuthenticatedUser = (state, userData) => {
  const hasAssignments = userData.hasOwnProperty("assignments");
  const assignments = !hasAssignments && !isNullOrEmpty(state.auth.user.assignments) ? copyObject(state.auth.user.assignments) : null;
  const user = userData.user;
  state.auth.user = user;

  if (hasAssignments) {
    state.auth.user.assignments = userData.assignments;
    state.auth.assignmentsCheckTime = new Date();
  }
  else if (assignments) {
    state.auth.user.assignments = assignments;
  }

  if(user.email !== null && user.emailConfirmed === true) {
    state.auth.authCheckTime = new Date();

    //let currentSeconds = state.auth.authCheckTime.getSeconds() + 5;
    //state.auth.authCheckTime.setSeconds(currentSeconds);

    let currentMinutes = state.auth.authCheckTime.getMinutes() + state.auth.authCheckInterval;
    state.auth.authCheckTime.setMinutes(currentMinutes);
  }
}

export const removeAuthenticatedUser = (state) => {
  state.auth = Object.assign({}, stateObjects.auth());
}

export const addRedirectRoute = (state, path) => {
  state.redirectRoute = path;
}

export const removeRedirectRoute = (state, path) => {
  state.redirectRoute = null;
}

export const setUserResponses = (state, payload) => {
  // Use the first element in state.surveyUser.responses as the format for param1 in Object.assign(param1, param2),
  // then use payload.responses as the source data for param2 so we can reproduce the same array of objects.
  //
  // Filter out 'undefines' in case there's corrupted data.
  //   NOTE: Only see this when purposely forcing a mismatch in calculating whether the user
  //         has responded to all questions between the server and the client during testing.
  //         So, this data corruption might be artificial.
  state.surveyUser.responses = Object.assign([state.surveyUser.responses[0]], payload.responses).filter(x => typeof x !== "undefined");
}

export const deleteResponses = (state, payload) => {
  let toDeleteIds = payload.hspCategoryQuestionIds;

  // Get the responses with question ID's that are not in the toDeleteIds list.
  state.surveyUser.responses = state.surveyUser.responses.filter(x => toDeleteIds.indexOf(x.hspCategoryQuestionId) < 0);
}
